/**
 * src/pages/Events.js
 * Browse captured event data with expandable inline detail rows.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import { usePlan } from '../context/PlanContext';

async function copyToClipboard(text, setter) {
  try {
    await navigator.clipboard.writeText(text);
    setter(true);
    setTimeout(() => setter(false), 2000);
  } catch {
    // Fallback: user can select text manually from the expanded section
  }
}

function formatDateOffset(daysAgo) {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function CopyIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}

const EXPORT_ROW_CAP = 10000;
const EXPORT_PAGE_SIZE = 200;

const CSV_COLUMNS = [
  { key: 'event_id', label: 'event_id' },
  { key: 'captured_at', label: 'captured_at' },
  { key: 'received_at', label: 'received_at' },
  { key: 'process_name', label: 'process_name' },
  { key: 'process_id', label: 'process_id' },
  { key: 'step_label', label: 'step_label' },
  { key: 'step_id', label: 'step_id' },
  { key: 'application_name', label: 'application_name' },
  { key: 'url', label: 'url' },
  { key: 'page_title', label: 'page_title' },
  { key: 'browser', label: 'browser' },
  { key: 'os', label: 'os' },
  { key: 'extension_version', label: 'extension_version' },
  { key: 'config_version', label: 'config_version' },
  { key: 'debug_event', label: 'debug_event' },
  { key: 'debug_suppressed', label: 'debug_suppressed' },
  { key: 'over_quota', label: 'over_quota' },
  { key: 'identifiers', label: 'identifiers', json: true },
  { key: 'custom_fields', label: 'custom_fields', json: true },
];

function escapeCsvValue(value) {
  if (value == null) return '';
  const str = String(value);
  if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
    return '"' + str.replace(/"/g, '""') + '"';
  }
  return str;
}

function buildCsvContent(events) {
  const header = CSV_COLUMNS.map(c => c.label).join(',');
  const rows = events.map(event =>
    CSV_COLUMNS.map(col => {
      const val = event[col.key];
      if (col.json) return escapeCsvValue(val ? JSON.stringify(val) : '');
      return escapeCsvValue(val);
    }).join(',')
  );
  return header + '\n' + rows.join('\n');
}

function downloadFile(content, filename, mimeType) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function formatExportTimestamp() {
  const d = new Date();
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
}

function DetailRow({ label, value, mono }) {
  return (
    <div className="detail-row">
      <span className="detail-row-label">{label}</span>
      <span className={`detail-row-value${mono ? ' font-mono' : ''}`}>{value || '\u2014'}</span>
    </div>
  );
}

function EventDetail({ event, isAdmin, onDelete, deletingEventId }) {
  const [showRawPayload, setShowRawPayload] = useState(false);
  const [showConfigSnapshot, setShowConfigSnapshot] = useState(false);
  const [copiedConfig, setCopiedConfig] = useState(false);
  const [copiedPayload, setCopiedPayload] = useState(false);
  const [confirmSingleDelete, setConfirmSingleDelete] = useState(false);

  const isDeleting = deletingEventId === event.event_id;

  return (
    <div className="event-detail">
      {isAdmin && (
        <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          {confirmSingleDelete ? (
            <>
              <span style={{ fontSize: '12px', color: 'var(--danger)' }}>Delete this event?</span>
              <button
                className="btn btn-danger btn-sm"
                onClick={() => onDelete(event.event_id)}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting\u2026' : 'Confirm'}
              </button>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => setConfirmSingleDelete(false)}
                disabled={isDeleting}
              >
                Cancel
              </button>
            </>
          ) : (
            <button
              className="btn btn-danger btn-sm"
              onClick={() => setConfirmSingleDelete(true)}
            >
              Delete event
            </button>
          )}
        </div>
      )}

      {event.debug_event && (
        <div style={{ marginBottom: '16px' }}>
          <span className="badge badge-warning">Debug Event</span>
          {event.debug_suppressed && (
            <span className="badge badge-danger" style={{ marginLeft: '6px' }}>Would Be Suppressed</span>
          )}
        </div>
      )}

      {event.over_quota && (
        <div style={{ marginBottom: '16px' }}>
          <span className="badge badge-warning">Over Quota</span>
          <span style={{ marginLeft: '8px', fontSize: '12px', color: 'var(--text-secondary)' }}>
            This event was captured after the monthly quota was exceeded.
          </span>
        </div>
      )}

      <div className="event-detail-grid">
        <div>
          <div className="event-detail-section-title">Event Metadata</div>
          <DetailRow label="Event ID" value={event.event_id} mono />
          <DetailRow label="Captured" value={new Date(event.captured_at).toLocaleString()} />
          <DetailRow label="Received" value={new Date(event.received_at).toLocaleString()} />
          <DetailRow label="Config Version" value={event.config_version} />
        </div>

        <div>
          <div className="event-detail-section-title">Process & Step</div>
          <DetailRow label="Process" value={`${event.process_name} (${event.process_id})`} />
          <DetailRow label="Step" value={`${event.step_label} (${event.step_id})`} />
          <DetailRow label="Application" value={event.application_name} />
        </div>

        <div>
          <div className="event-detail-section-title">Page</div>
          <DetailRow label="URL" value={event.url} mono />
          <DetailRow label="Page Title" value={event.page_title} />
        </div>

        <div>
          <div className="event-detail-section-title">Browser</div>
          <DetailRow label="Browser" value={[event.browser, event.browser_version].filter(Boolean).join(' ') || null} />
          <DetailRow label="OS" value={event.os} />
          <DetailRow label="Extension" value={event.extension_version} />
        </div>

        {(event.trigger_tag || event.trigger_id || event.trigger_text) && (
          <div>
            <div className="event-detail-section-title">Trigger Element</div>
            <DetailRow label="Tag" value={event.trigger_tag} />
            <DetailRow label="ID" value={event.trigger_id} mono />
            <DetailRow label="Classes" value={event.trigger_classes?.join(', ')} mono />
            <DetailRow label="Text" value={event.trigger_text} />
          </div>
        )}
      </div>

      {event.identifiers && Object.keys(event.identifiers).length > 0 && (
        <div style={{ marginTop: '20px' }}>
          <div className="event-detail-section-title">Identifiers</div>
          {Object.entries(event.identifiers).map(([key, value]) => (
            <DetailRow key={key} label={key} value={value || '(empty)'} mono />
          ))}
        </div>
      )}

      {event.custom_fields && Object.keys(event.custom_fields).length > 0 && (
        <div style={{ marginTop: '20px' }}>
          <div className="event-detail-section-title">Custom Fields</div>
          {Object.entries(event.custom_fields).map(([key, value]) => (
            <DetailRow key={key} label={key} value={typeof value === 'object' ? JSON.stringify(value) : String(value)} mono />
          ))}
        </div>
      )}

      {event.config_snapshot && (
        <div style={{ marginTop: '20px' }}>
          <div className="flex items-center gap-8" style={{ marginBottom: '8px' }}>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => setShowConfigSnapshot(!showConfigSnapshot)}
            >
              {showConfigSnapshot ? '\u25be Hide' : '\u25b8 Show'} Config Snapshot
            </button>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => copyToClipboard(JSON.stringify(event.config_snapshot, null, 2), setCopiedConfig)}
              title="Copy to clipboard"
            >
              {copiedConfig ? 'Copied!' : <CopyIcon />}
            </button>
          </div>
          {showConfigSnapshot && (
            <pre className="event-detail-json">{JSON.stringify(event.config_snapshot, null, 2)}</pre>
          )}
        </div>
      )}

      {event.raw_payload && (
        <div style={{ marginTop: '20px' }}>
          <div className="flex items-center gap-8" style={{ marginBottom: '8px' }}>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => setShowRawPayload(!showRawPayload)}
            >
              {showRawPayload ? '\u25be Hide' : '\u25b8 Show'} Raw Payload
            </button>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => copyToClipboard(JSON.stringify(event.raw_payload, null, 2), setCopiedPayload)}
              title="Copy to clipboard"
            >
              {copiedPayload ? 'Copied!' : <CopyIcon />}
            </button>
          </div>
          {showRawPayload && (
            <pre className="event-detail-json">{JSON.stringify(event.raw_payload, null, 2)}</pre>
          )}
        </div>
      )}

    </div>
  );
}

/* ─── Memoized summary bar ─────────────────────────────────────────────── */

const SummaryBar = React.memo(function SummaryBar({
  summary, summaryLoading, hasActiveFilters, events, hasMore,
  isAdmin, selectedEventIds, confirmDelete, deleting, handleDeleteSelected, setConfirmDelete,
  autoRefreshSeconds, autoRefreshEnabled, setAutoRefreshEnabled,
  expandedEventId, exporting, exportProgress, cancelExport, fetchAllAndExport,
  summaryExpanded, setSummaryExpanded, summaryHasBreakdown,
  canExport, canAutoRefresh,
}) {
  const showSummary = (summary && summary.total > 0) || events.length > 0;
  if (!showSummary) return null;

  return (
    <div className="filter-summary">
      <span className="filter-summary-text">
        {autoRefreshSeconds > 0 && autoRefreshEnabled && !expandedEventId && !exporting && selectedEventIds.size === 0 && (
          <span className="live-indicator">
            <span className="live-dot" />
            Live
          </span>
        )}
        {autoRefreshSeconds > 0 && autoRefreshEnabled && (expandedEventId || exporting || selectedEventIds.size > 0) && (
          <span className="live-indicator live-indicator-paused">
            <span className="live-dot-paused" />
            Paused
          </span>
        )}
        {summaryLoading ? (
          'Loading summary\u2026'
        ) : summary ? (
          <>
            <strong>{summary.total.toLocaleString()}</strong>&nbsp;event{summary.total !== 1 ? 's' : ''}
            {hasActiveFilters && ' (filtered)'}
            {summaryHasBreakdown && (
              <>
                {' \u00b7 '}
                <button
                  className="btn-link-subtle"
                  onClick={() => setSummaryExpanded(prev => !prev)}
                >
                  {summaryExpanded ? 'Hide breakdown \u25be' : 'Show breakdown \u25b8'}
                </button>
              </>
            )}
          </>
        ) : (
          <>
            Showing {events.length} event{events.length !== 1 ? 's' : ''}
            {hasActiveFilters && ' (filtered)'}
            {hasMore && ' \u00b7 more available'}
          </>
        )}
      </span>
      <span className="export-buttons">
        {isAdmin && selectedEventIds.size > 0 && (
          <span style={{ marginRight: '12px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            {confirmDelete ? (
              <>
                <span style={{ fontSize: '12px', color: 'var(--danger)' }}>
                  Delete {selectedEventIds.size} event{selectedEventIds.size !== 1 ? 's' : ''}?
                </span>
                <button
                  className="btn btn-danger btn-sm"
                  onClick={handleDeleteSelected}
                  disabled={deleting}
                >
                  {deleting ? 'Deleting\u2026' : 'Confirm'}
                </button>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => setConfirmDelete(false)}
                  disabled={deleting}
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                className="btn btn-danger btn-sm"
                onClick={() => setConfirmDelete(true)}
              >
                Delete ({selectedEventIds.size})
              </button>
            )}
          </span>
        )}
        {canAutoRefresh !== false && autoRefreshSeconds > 0 && (
          <label className="toggle" style={{ marginRight: '12px' }}>
            <input
              type="checkbox"
              checked={autoRefreshEnabled}
              onChange={() => setAutoRefreshEnabled(prev => !prev)}
            />
            <div className="toggle-track" />
            <div className="toggle-thumb" />
            <span className="toggle-label">
              Auto-refresh
              {autoRefreshEnabled && (
                <span className="auto-refresh-countdown"> (every {autoRefreshSeconds}s)</span>
              )}
            </span>
          </label>
        )}
        {canExport !== false && (
          exporting ? (
            <>
              <span className="export-progress">
                Exporting\u2026 {exportProgress?.fetched || 0} events
              </span>
              <button className="btn btn-ghost btn-sm" onClick={cancelExport}>Cancel</button>
            </>
          ) : exportProgress?.capped ? (
            <span className="export-progress">
              Export limited to {EXPORT_ROW_CAP.toLocaleString()} rows. Narrow your filters for smaller datasets.
            </span>
          ) : (
            <>
              <button className="btn btn-secondary btn-sm" onClick={() => fetchAllAndExport('csv')}>Export CSV</button>
              <button className="btn btn-secondary btn-sm" onClick={() => fetchAllAndExport('json')}>Export JSON</button>
            </>
          )
        )}
      </span>
    </div>
  );
});

/* ─── Collapsible aggregation breakdown ─────────────────────────────── */

const SummaryBreakdown = React.memo(function SummaryBreakdown({
  summary, summaryExpanded,
}) {
  if (!summaryExpanded || !summary || summary.processes.length === 0) return null;

  return (
    <div className="summary-breakdown-card card">
      <div className="table-wrapper">
        <table className="summary-breakdown-table">
          <thead>
            <tr>
              <th>Process</th>
              <th style={{ textAlign: 'right' }}>Count</th>
              <th>Steps</th>
            </tr>
          </thead>
          <tbody>
            {summary.processes.map(p => (
              <tr key={p.process_id}>
                <td className="summary-breakdown-process">{p.process_name}</td>
                <td className="summary-breakdown-count">{p.count.toLocaleString()}</td>
                <td className="summary-breakdown-steps">
                  {p.steps.map((s, i) => (
                    <span key={s.step_id}>
                      {i > 0 && ', '}
                      {s.step_label} ({s.count.toLocaleString()})
                    </span>
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
});

/* ─── Memoized events table ────────────────────────────────────────────── */

const EventsTable = React.memo(function EventsTable({
  events, isAdmin, selectedEventIds, expandedEventId,
  toggleExpand, toggleSelectEvent, toggleSelectAll,
  sortBy, sortOrder, handleSort, colCount,
  hasMore, loadingMore, handleLoadMore,
  handleDeleteSingle, deletingEventId, hasActiveFilters,
  refreshing,
}) {
  if (events.length === 0) {
    return (
      <div className="card">
        <div className="empty-state">
          <div className="empty-state-icon" style={{ opacity: 0.4 }}>&#128202;</div>
          <div className="empty-state-title">No events found</div>
          <div className="empty-state-text">
            {hasActiveFilters
              ? 'Try adjusting your filters or clearing them.'
              : 'No events have been captured yet.'}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card" style={refreshing ? { opacity: 0.6, pointerEvents: 'none', transition: 'opacity 0.15s' } : undefined}>
      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              {isAdmin && (
                <th className="event-checkbox-cell" style={{ width: '32px' }}>
                  <input
                    type="checkbox"
                    checked={events.length > 0 && selectedEventIds.size === events.length}
                    onChange={toggleSelectAll}
                    title="Select all"
                  />
                </th>
              )}
              <th style={{ width: '28px' }}></th>
              <th className="th-sortable" onClick={() => handleSort('captured_at')}>
                Captured {sortBy === 'captured_at' && <span className="sort-arrow">{sortOrder === 'asc' ? '\u25b2' : '\u25bc'}</span>}
              </th>
              <th className="th-sortable" onClick={() => handleSort('process_name')}>
                Process {sortBy === 'process_name' && <span className="sort-arrow">{sortOrder === 'asc' ? '\u25b2' : '\u25bc'}</span>}
              </th>
              <th className="th-sortable" onClick={() => handleSort('step_label')}>
                Step {sortBy === 'step_label' && <span className="sort-arrow">{sortOrder === 'asc' ? '\u25b2' : '\u25bc'}</span>}
              </th>
              <th>Application</th>
              <th>URL</th>
            </tr>
          </thead>
          <tbody>
            {events.map(event => (
              <React.Fragment key={event.event_id}>
                <tr
                  onClick={() => toggleExpand(event.event_id)}
                  style={{ cursor: 'pointer' }}
                  className={expandedEventId === event.event_id ? 'event-row-expanded' : ''}
                >
                  {isAdmin && (
                    <td className="event-checkbox-cell" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedEventIds.has(event.event_id)}
                        onChange={(e) => toggleSelectEvent(event.event_id, e)}
                      />
                    </td>
                  )}
                  <td style={{ width: '28px', textAlign: 'center' }}>
                    <span className={`expand-arrow${expandedEventId === event.event_id ? ' expanded' : ''}`}>
                      &#9654;
                    </span>
                  </td>
                  <td className="td-mono">{new Date(event.captured_at).toLocaleString()}</td>
                  <td>{event.process_name}</td>
                  <td>
                    {event.step_label}
                    {event.debug_event && (
                      <span className="badge badge-warning" style={{ marginLeft: '6px' }}>Debug</span>
                    )}
                    {event.debug_suppressed && (
                      <span className="badge badge-danger" style={{ marginLeft: '6px' }}>Suppressed</span>
                    )}
                    {event.over_quota && (
                      <span className="badge badge-warning" style={{ marginLeft: '6px' }}>Over quota</span>
                    )}
                  </td>
                  <td>{event.application_name}</td>
                  <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {event.url}
                  </td>
                </tr>
                {expandedEventId === event.event_id && (
                  <tr className="event-detail-row">
                    <td colSpan={colCount} style={{ padding: 0 }}>
                      <EventDetail event={event} isAdmin={isAdmin} onDelete={handleDeleteSingle} deletingEventId={deletingEventId} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {hasMore && (
        <div style={{ padding: '16px 24px', textAlign: 'center', borderTop: '1px solid var(--border)' }}>
          <button
            className="btn btn-secondary"
            onClick={handleLoadMore}
            disabled={loadingMore}
          >
            {loadingMore ? 'Loading\u2026' : 'Load more'}
          </button>
        </div>
      )}
    </div>
  );
});

/* ─── Main Events page ─────────────────────────────────────────────────── */

export default function Events() {
  const { user } = useAuth();
  const { hasFeature } = usePlan();
  const isAdmin = user?.role === 'admin';
  const canExport = hasFeature('export');
  const canAutoRefresh = hasFeature('auto_refresh');
  const [events, setEvents] = useState([]);
  const [processes, setProcesses] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [expandedEventId, setExpandedEventId] = useState(null);
  // Selection state (admin only)
  const [selectedEventIds, setSelectedEventIds] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deletingEventId, setDeletingEventId] = useState(null);

  // Filter state
  const [filterProcessId, setFilterProcessId] = useState('');
  const [filterStepId, setFilterStepId] = useState('');
  const [filterUrl, setFilterUrl] = useState('');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');
  const [filterDebugFilter, setFilterDebugFilter] = useState('default');
  const [steps, setSteps] = useState([]);
  const [allSteps, setAllSteps] = useState([]);
  // Sort state
  const [sortBy, setSortBy] = useState('captured_at');
  const [sortOrder, setSortOrder] = useState('desc');
  // Applied filters (used for actual API calls)
  const [appliedFilters, setAppliedFilters] = useState({ process_id: '', step_id: '', url: '', from: '', to: '', debug_filter: 'default' });
  // Summary state
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryExpanded, setSummaryExpanded] = useState(false);
  // Export state
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(null);
  const exportAbortRef = useRef(null);
  // Auto-refresh state
  const [autoRefreshSeconds, setAutoRefreshSeconds] = useState(0);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(false);
  const autoRefreshTimerRef = useRef(null);

  // Refs for latest values in callbacks
  const appliedFiltersRef = useRef(appliedFilters);
  appliedFiltersRef.current = appliedFilters;
  const sortRef = useRef({ sortBy, sortOrder });
  sortRef.current = { sortBy, sortOrder };

  const hasActiveFilters = appliedFilters.process_id || appliedFilters.step_id || appliedFilters.url || appliedFilters.from || appliedFilters.to || (appliedFilters.debug_filter && appliedFilters.debug_filter !== 'default');

  const summaryHasBreakdown = summary && (summary.processes.length > 1 || (summary.processes.length === 1 && summary.processes[0].steps.length > 1));

  useEffect(() => {
    fetchProcesses();
    fetchAllSteps();
    fetchAutoRefreshSetting();
    fetchEvents(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-refresh polling
  useEffect(() => {
    if (autoRefreshTimerRef.current) {
      clearInterval(autoRefreshTimerRef.current);
      autoRefreshTimerRef.current = null;
    }

    const paused = expandedEventId !== null || exporting || selectedEventIds.size > 0 || deletingEventId !== null;
    if (autoRefreshEnabled && autoRefreshSeconds > 0 && !paused) {
      autoRefreshTimerRef.current = setInterval(() => {
        fetchEvents(true);
      }, autoRefreshSeconds * 1000);
    }

    return () => {
      if (autoRefreshTimerRef.current) clearInterval(autoRefreshTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefreshEnabled, autoRefreshSeconds, expandedEventId, exporting, selectedEventIds.size, deletingEventId]);

  async function fetchAutoRefreshSetting() {
    try {
      const res = await api.get('/portal/settings');
      setAutoRefreshSeconds(res.data.auto_refresh_seconds || 0);
    } catch {
      // Non-critical — auto-refresh toggle just won't appear
    }
  }

  async function fetchProcesses() {
    try {
      const res = await api.get('/portal/events/processes');
      setProcesses(res.data);
    } catch (err) {
      // Non-critical — dropdown just won't have options
    }
  }

  async function fetchAllSteps() {
    try {
      const res = await api.get('/portal/events/steps');
      setAllSteps(res.data);
      setSteps(res.data);
    } catch (err) {
      // Non-critical — dropdown just won't have options
    }
  }

  async function fetchSummary(filters = appliedFilters) {
    setSummaryLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.process_id) params.set('process_id', filters.process_id);
      if (filters.step_id) params.set('step_id', filters.step_id);
      if (filters.url) params.set('url', filters.url);
      if (filters.from) params.set('from', filters.from);
      if (filters.to) params.set('to', filters.to);
      if (filters.debug_filter && filters.debug_filter !== 'default') params.set('debug_filter', filters.debug_filter);
      const res = await api.get(`/portal/events/summary?${params.toString()}`);
      setSummary(res.data);
    } catch {
      setSummary(null);
    } finally {
      setSummaryLoading(false);
    }
  }

  function applyFiltersWithOverrides(overrides = {}) {
    const newFilters = {
      process_id: filterProcessId,
      step_id: filterStepId,
      url: filterUrl,
      from: filterFrom,
      to: filterTo,
      debug_filter: filterDebugFilter,
      ...overrides
    };
    setAppliedFilters(newFilters);
    fetchEvents(true, newFilters);
  }

  function handleProcessChange(newProcessId) {
    setFilterProcessId(newProcessId);
    setFilterStepId('');
    if (newProcessId) {
      setSteps(allSteps.filter(s => s.process_id === newProcessId));
    } else {
      setSteps(allSteps);
    }
    applyFiltersWithOverrides({ process_id: newProcessId, step_id: '' });
  }

  function handleStepChange(newStepId) {
    setFilterStepId(newStepId);
    let processId = filterProcessId;
    if (newStepId) {
      const step = allSteps.find(s => s.step_id === newStepId);
      if (step && step.process_id !== filterProcessId) {
        processId = step.process_id;
        setFilterProcessId(processId);
        setSteps(allSteps.filter(s => s.process_id === processId));
      }
    }
    applyFiltersWithOverrides({ step_id: newStepId, process_id: processId });
  }

  async function fetchEvents(resetCursor = true, filters = appliedFilters, sort = { sortBy, sortOrder }) {
    if (resetCursor) {
      setRefreshing(true);
      setCursor(null);
      setExpandedEventId(null);
      setSelectedEventIds(new Set());
      setConfirmDelete(false);
      fetchSummary(filters);
    } else {
      setLoadingMore(true);
    }
    setError('');

    try {
      const params = new URLSearchParams();
      if (filters.process_id) params.set('process_id', filters.process_id);
      if (filters.step_id) params.set('step_id', filters.step_id);
      if (filters.url) params.set('url', filters.url);
      if (filters.from) params.set('from', filters.from);
      if (filters.to) params.set('to', filters.to);
      if (filters.debug_filter && filters.debug_filter !== 'default') params.set('debug_filter', filters.debug_filter);
      params.set('sort_by', sort.sortBy);
      params.set('sort_order', sort.sortOrder);
      if (!resetCursor && cursor) params.set('cursor', cursor);

      const res = await api.get(`/portal/events?${params.toString()}`);
      const { data, pagination } = res.data;

      if (resetCursor) {
        setEvents(data);
      } else {
        setEvents(prev => [...prev, ...data]);
      }
      setCursor(pagination.next_cursor);
      setHasMore(pagination.has_more);
    } catch (err) {
      if (!err.response) {
        setError('Unable to reach the server. Check your connection and try again.');
      } else {
        setError(err.response.data?.message || 'Failed to load events.');
      }
    } finally {
      setInitialLoading(false);
      setRefreshing(false);
      setLoadingMore(false);
    }
  }

  function handleApplyFilters() {
    const newFilters = { process_id: filterProcessId, step_id: filterStepId, url: filterUrl, from: filterFrom, to: filterTo, debug_filter: filterDebugFilter };
    setAppliedFilters(newFilters);
    fetchEvents(true, newFilters);
  }

  function handleClearFilters() {
    setFilterProcessId('');
    setFilterStepId('');
    setSteps(allSteps);
    setFilterUrl('');
    setFilterFrom('');
    setFilterTo('');
    setFilterDebugFilter('default');
    setSortBy('captured_at');
    setSortOrder('desc');
    const emptyFilters = { process_id: '', step_id: '', url: '', from: '', to: '', debug_filter: 'default' };
    setAppliedFilters(emptyFilters);
    fetchEvents(true, emptyFilters, { sortBy: 'captured_at', sortOrder: 'desc' });
  }

  function handlePreset(daysAgo) {
    const from = formatDateOffset(daysAgo);
    const to = formatDateOffset(0);
    setFilterFrom(from);
    setFilterTo(to);
    const newFilters = { process_id: filterProcessId, step_id: filterStepId, url: filterUrl, from, to, debug_filter: filterDebugFilter };
    setAppliedFilters(newFilters);
    fetchEvents(true, newFilters);
  }

  function handleTimePreset(minutesAgo) {
    const now = new Date();
    const from = new Date(now.getTime() - minutesAgo * 60000).toISOString();
    const to = now.toISOString();
    // Date inputs show today for visual feedback; API gets precise ISO timestamps
    const today = formatDateOffset(0);
    setFilterFrom(today);
    setFilterTo(today);
    const newFilters = { process_id: filterProcessId, step_id: filterStepId, url: filterUrl, from, to, debug_filter: filterDebugFilter };
    setAppliedFilters(newFilters);
    fetchEvents(true, newFilters);
  }

  const handleLoadMore = useCallback(() => {
    fetchEvents(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cursor, appliedFilters, sortBy, sortOrder]);

  const handleSort = useCallback((column) => {
    let newOrder;
    if (sortRef.current.sortBy === column) {
      newOrder = sortRef.current.sortOrder === 'desc' ? 'asc' : 'desc';
    } else {
      newOrder = 'desc';
    }
    setSortBy(column);
    setSortOrder(newOrder);
    fetchEvents(true, appliedFiltersRef.current, { sortBy: column, sortOrder: newOrder });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleFilterKeyDown(e) {
    if (e.key === 'Enter') {
      handleApplyFilters();
    }
  }

  async function fetchAllAndExport(format) {
    const abortController = new AbortController();
    exportAbortRef.current = abortController;
    setExporting(true);
    setExportProgress({ fetched: 0 });

    const allEvents = [];
    let pageCursor = null;
    let capped = false;

    try {
      while (true) {
        if (abortController.signal.aborted) return;

        const params = new URLSearchParams();
        if (appliedFilters.process_id) params.set('process_id', appliedFilters.process_id);
        if (appliedFilters.step_id) params.set('step_id', appliedFilters.step_id);
        if (appliedFilters.url) params.set('url', appliedFilters.url);
        if (appliedFilters.from) params.set('from', appliedFilters.from);
        if (appliedFilters.to) params.set('to', appliedFilters.to);
        if (appliedFilters.debug_filter && appliedFilters.debug_filter !== 'default') params.set('debug_filter', appliedFilters.debug_filter);
        params.set('sort_by', sortBy);
        params.set('sort_order', sortOrder);
        params.set('limit', String(EXPORT_PAGE_SIZE));
        if (pageCursor) params.set('cursor', pageCursor);

        const res = await api.get(`/portal/events?${params.toString()}`, {
          signal: abortController.signal,
        });
        const { data, pagination } = res.data;

        allEvents.push(...data);
        setExportProgress({ fetched: allEvents.length });

        if (allEvents.length >= EXPORT_ROW_CAP) {
          allEvents.length = EXPORT_ROW_CAP;
          capped = true;
          break;
        }
        if (!pagination.has_more) break;
        pageCursor = pagination.next_cursor;
      }

      if (abortController.signal.aborted) return;

      const timestamp = formatExportTimestamp();
      if (format === 'csv') {
        downloadFile(buildCsvContent(allEvents), `events-${timestamp}.csv`, 'text/csv;charset=utf-8;');
      } else {
        downloadFile(JSON.stringify(allEvents, null, 2), `events-${timestamp}.json`, 'application/json');
      }

      if (capped) {
        setExportProgress({ fetched: allEvents.length, capped: true });
        setTimeout(() => setExportProgress(null), 4000);
      } else {
        setExportProgress(null);
      }
    } catch (err) {
      if (abortController.signal.aborted) return;
      if (allEvents.length > 0) {
        const timestamp = formatExportTimestamp();
        if (format === 'csv') {
          downloadFile(buildCsvContent(allEvents), `events-partial-${timestamp}.csv`, 'text/csv;charset=utf-8;');
        } else {
          downloadFile(JSON.stringify(allEvents, null, 2), `events-partial-${timestamp}.json`, 'application/json');
        }
      }
      setExportProgress(null);
    } finally {
      setExporting(false);
    }
  }

  function cancelExport() {
    if (exportAbortRef.current) {
      exportAbortRef.current.abort();
    }
    setExporting(false);
    setExportProgress(null);
  }

  const toggleExpand = useCallback((eventId) => {
    setExpandedEventId(prev => prev === eventId ? null : eventId);
  }, []);

  // ─── Selection handlers (admin only) ──────────────────────────────

  const toggleSelectEvent = useCallback((eventId, e) => {
    e.stopPropagation();
    setSelectedEventIds(prev => {
      const next = new Set(prev);
      if (next.has(eventId)) next.delete(eventId);
      else next.add(eventId);
      return next;
    });
    setConfirmDelete(false);
  }, []);

  const toggleSelectAll = useCallback((e) => {
    e.stopPropagation();
    setSelectedEventIds(prev => {
      // Read current events from closure — this will be stable per render cycle
      return prev.size === events.length ? new Set() : new Set(events.map(ev => ev.event_id));
    });
    setConfirmDelete(false);
  }, [events]);

  async function handleDeleteSelected() {
    if (selectedEventIds.size === 0) return;
    setDeleting(true);
    setError('');
    try {
      await api.delete('/portal/events', { data: { event_ids: [...selectedEventIds] } });
      setSelectedEventIds(new Set());
      setConfirmDelete(false);
      fetchEvents(true);
    } catch (err) {
      if (!err.response) {
        setError('Unable to reach the server. Check your connection and try again.');
      } else {
        setError(err.response.data?.error || 'Failed to delete events.');
      }
    } finally {
      setDeleting(false);
    }
  }

  const handleDeleteSingle = useCallback(async (eventId) => {
    setDeletingEventId(eventId);
    setError('');
    try {
      await api.delete('/portal/events', { data: { event_ids: [eventId] } });
      setEvents(prev => prev.filter(e => e.event_id !== eventId));
      setExpandedEventId(null);
      setSelectedEventIds(prev => {
        const next = new Set(prev);
        next.delete(eventId);
        return next;
      });
      fetchSummary();
    } catch (err) {
      if (!err.response) {
        setError('Unable to reach the server. Check your connection and try again.');
      } else {
        setError(err.response.data?.error || 'Failed to delete event.');
      }
    } finally {
      setDeletingEventId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const colCount = isAdmin ? 7 : 6;

  if (initialLoading) {
    return (
      <div className="page">
        <div style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Loading\u2026</div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">Events</h1>
        <p className="page-subtitle">Browse captured event data across your tracked processes.</p>
      </div>

      {error && (
        <div className="alert alert-error">
          {error}
          <button className="alert-retry" onClick={() => fetchEvents(true)}>Try again</button>
        </div>
      )}

      {/* Filter bar */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <div className="card-body" style={{ padding: '16px 24px' }}>
          <div className="flex gap-16" style={{ flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ marginBottom: 0, flex: '0 1 auto', minWidth: '150px' }}>
              <label className="form-label">Process</label>
              <select
                className="form-select"
                value={filterProcessId}
                onChange={e => handleProcessChange(e.target.value)}
              >
                <option value="">All processes</option>
                {processes.map(p => (
                  <option key={p.process_id} value={p.process_id}>{p.process_name}</option>
                ))}
              </select>
            </div>
            <div style={{ marginBottom: 0, flex: '0 1 auto', minWidth: '140px' }}>
              <label className="form-label">Step</label>
              <select
                className="form-select"
                value={filterStepId}
                onChange={e => handleStepChange(e.target.value)}
              >
                <option value="">All steps</option>
                {steps.map(s => (
                  <option key={s.step_id} value={s.step_id}>{s.step_label}</option>
                ))}
              </select>
            </div>
            <div style={{ marginBottom: 0, flex: '1 1 150px', minWidth: '150px', maxWidth: '300px' }}>
              <label className="form-label">URL</label>
              <input
                className="form-input"
                type="text"
                placeholder="Filter by URL..."
                value={filterUrl}
                onChange={e => setFilterUrl(e.target.value)}
                onKeyDown={handleFilterKeyDown}
              />
            </div>
            <div style={{ marginBottom: 0, flex: '0 0 auto' }}>
              <label className="form-label">From</label>
              <input
                className="form-input"
                type="date"
                value={filterFrom}
                onChange={e => { setFilterFrom(e.target.value); applyFiltersWithOverrides({ from: e.target.value }); }}
              />
            </div>
            <div style={{ marginBottom: 0, flex: '0 0 auto' }}>
              <label className="form-label">To</label>
              <input
                className="form-input"
                type="date"
                value={filterTo}
                onChange={e => { setFilterTo(e.target.value); applyFiltersWithOverrides({ to: e.target.value }); }}
              />
            </div>
            <div style={{ marginBottom: 0, flex: '0 1 auto', minWidth: '140px' }}>
              <label className="form-label">Debug</label>
              <select
                className="form-select"
                value={filterDebugFilter}
                onChange={e => { setFilterDebugFilter(e.target.value); applyFiltersWithOverrides({ debug_filter: e.target.value }); }}
              >
                <option value="default">Hide suppressed</option>
                <option value="all">Show all</option>
                <option value="suppressed">Only suppressed</option>
              </select>
            </div>
            <div className="filter-bar-control" style={{ gap: '8px' }}>
              <button className="btn btn-primary btn-sm" onClick={handleApplyFilters}>Search</button>
              {(hasActiveFilters || filterProcessId || filterStepId || filterUrl || filterFrom || filterTo || filterDebugFilter !== 'default') && (
                <button className="btn btn-ghost btn-sm" onClick={handleClearFilters}>Clear filters</button>
              )}
            </div>
          </div>
          <div className="filter-presets">
            <span className="filter-presets-label">Quick:</span>
            <button className="btn btn-ghost btn-sm" onClick={() => handleTimePreset(15)}>Last 15 min</button>
            <button className="btn btn-ghost btn-sm" onClick={() => handleTimePreset(60)}>Last hour</button>
            <button className="btn btn-ghost btn-sm" onClick={() => handlePreset(1)}>Last 24 hours</button>
            <button className="btn btn-ghost btn-sm" onClick={() => handlePreset(7)}>Last 7 days</button>
            <button className="btn btn-ghost btn-sm" onClick={() => handlePreset(30)}>Last 30 days</button>
          </div>
        </div>
      </div>

      {/* Result count + export */}
      <SummaryBar
        summary={summary}
        summaryLoading={summaryLoading}
        hasActiveFilters={hasActiveFilters}
        events={events}
        hasMore={hasMore}
        isAdmin={isAdmin}
        selectedEventIds={selectedEventIds}
        confirmDelete={confirmDelete}
        deleting={deleting}
        handleDeleteSelected={handleDeleteSelected}
        setConfirmDelete={setConfirmDelete}
        autoRefreshSeconds={autoRefreshSeconds}
        autoRefreshEnabled={autoRefreshEnabled}
        setAutoRefreshEnabled={setAutoRefreshEnabled}
        expandedEventId={expandedEventId}
        exporting={exporting}
        exportProgress={exportProgress}
        cancelExport={cancelExport}
        fetchAllAndExport={fetchAllAndExport}
        summaryExpanded={summaryExpanded}
        setSummaryExpanded={setSummaryExpanded}
        summaryHasBreakdown={summaryHasBreakdown}
        canExport={canExport}
        canAutoRefresh={canAutoRefresh}
      />

      {/* Collapsible aggregation breakdown */}
      <SummaryBreakdown
        summary={summary}
        summaryExpanded={summaryExpanded}
      />

      {/* Events table */}
      <EventsTable
        events={events}
        isAdmin={isAdmin}
        selectedEventIds={selectedEventIds}
        expandedEventId={expandedEventId}
        toggleExpand={toggleExpand}
        toggleSelectEvent={toggleSelectEvent}
        toggleSelectAll={toggleSelectAll}
        sortBy={sortBy}
        sortOrder={sortOrder}
        handleSort={handleSort}
        colCount={colCount}
        hasMore={hasMore}
        loadingMore={loadingMore}
        handleLoadMore={handleLoadMore}
        handleDeleteSingle={handleDeleteSingle}
        deletingEventId={deletingEventId}
        hasActiveFilters={hasActiveFilters}
        refreshing={refreshing}
      />
    </div>
  );
}
